import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Search, Calendar, MapPin, Users, Loader2, SlidersHorizontal, X, Ticket,
  ChevronLeft, ChevronRight, Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { academyAPI, sportTypeAPI } from '../../services/api';

/**
 * Find and book a session.
 *
 * One component, two placements. Mounted with no `academyId` it is the global
 * feed — "open play near me this week". Mounted with one, it is the same search
 * scoped to that academy, and the academy name drops out of the results because
 * repeating it on every row is noise once you are already inside it.
 *
 * Sessions are the browsable unit, not offerings. Nobody searches for
 * "Wednesday Open Play — Beginner"; they search for a night with spots left at
 * a price they will pay, and the offering is just how it got there.
 */

const LEVELS = [
  { value: '', label: 'Any level' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

const TYPES = [
  { value: '', label: 'All' },
  { value: 'open_play', label: 'Open play' },
  { value: 'meetup', label: 'Meetups' },
];

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const money = (amount, currency = 'USD') =>
  amount === 0
    ? 'Free'
    : new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);

// Matches what the host set on the price. "/person" on a monthly pass would
// misstate what is being bought, so the unit follows the charge model.
// Monday-first for display; the server keys on the three-letter codes.
const DAYS = [
  { code: 'MON', label: 'Mon' }, { code: 'TUE', label: 'Tue' }, { code: 'WED', label: 'Wed' },
  { code: 'THU', label: 'Thu' }, { code: 'FRI', label: 'Fri' }, { code: 'SAT', label: 'Sat' },
  { code: 'SUN', label: 'Sun' },
];

const PRICE_UNIT = {
  per_session: '/person',
  one_off:     '/person',
  monthly:     '/month',
  full_term:   '/term',
};

// What a booking actually costs, once the venue's online booking fee is added.
// Shown separately rather than folded into the headline price: the fee is not
// charged at the counter, so the two are different numbers.
const feeLine = (price) => {
  if (!price?.bookingFee) return null;
  const { type, value } = price.bookingFee;
  const extra = type === 'percent' ? (Number(price.amount) * Number(value)) / 100 : Number(value);
  if (!Number.isFinite(extra) || extra <= 0) return null;
  return `${money(Number(price.amount) + extra, price.currency)} online`;
};

// Going and waiting render the same way; only the heading and the tone differ.
// Name and face, nothing else — the heading already carries the count, and the
// order is first-come, so a rank on every row would be noise.
const RosterList = ({ heading, people, empty, theme, muted = false }) => (
  <div>
    <p className={`text-[11px] font-medium ${muted ? 'text-amber-600 dark:text-amber-400' : theme.textSecondary} mb-1`}>
      {heading}
    </p>
    {people.length === 0 ? (
      <p className={`text-xs ${theme.textMuted}`}>{empty}</p>
    ) : (
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {people.map((p, i) => (
          <span key={p.id || i} className={`text-xs ${theme.text} flex items-center gap-1.5`}>
            {p.avatar
              ? <img src={p.avatar} alt="" className="w-4 h-4 rounded-full object-cover" />
              : <span className={`w-4 h-4 rounded-full ${theme.backgroundSecondary} text-[9px] flex items-center justify-center ${theme.textMuted}`}>
                  {p.name.charAt(0).toUpperCase()}
                </span>}
            {p.name}
          </span>
        ))}
      </div>
    )}
  </div>
);

const SessionFinder = ({ academyId = null, onBook, title, compact = false, refreshKey = 0 }) => {
  const { theme } = useTheme();
  const scoped = Boolean(academyId);

  const [filters, setFilters] = useState({
    from: todayKey(), to: '', sportTypeId: '', level: '', programType: '', maxPrice: '', hideFull: false, days: [],
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sports, setSports] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  // A floor rather than a total when the server hit its scan cap while applying
  // a price or fullness filter.
  const [truncated, setTruncated] = useState(false);
  // Rosters, by session id, fetched only when someone opens one. Loading the
  // whole page's worth up front would be a query per row for something most
  // people never look at.
  const [openRoster, setOpenRoster] = useState(null);
  const [rosters, setRosters] = useState({});
  // Which place is being given up, and for whom. One at a time.
  const [cancelling, setCancelling] = useState(null);   // {sessionId, player}
  const [releasing, setReleasing] = useState(null);

  const PAGE_SIZE = 10;

  useEffect(() => {
    sportTypeAPI.getAll().then(res => setSports(res?.data || res || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Every filter goes to the server, including price and fullness: paging
      // a client-side filter shows a short page and a total that counts rows
      // the filter would have dropped.
      const res = await academyAPI.sessions.discover({
        ...(academyId ? { academyId } : {}),
        ...filters,
        // An array would serialise as days=MON&days=WED; the server reads one
        // comma-separated value.
        days: filters.days.length ? filters.days.join(',') : '',
        page: compact ? 1 : page,
        limit: compact ? 6 : PAGE_SIZE,
      });
      setSessions(res?.sessions || []);
      setTotal(res?.total ?? 0);
      setTruncated(Boolean(res?.truncated));
      setError(null);
    } catch (err) {
      console.error('[SessionFinder] load failed', err);
      setError(err?.message || 'Could not load sessions');
    } finally {
      setLoading(false);
    }
  }, [academyId, filters, compact, page, refreshKey]);

  useEffect(() => {
    const t = setTimeout(load, 200);   // debounced so typing a price is not a request per keystroke
    return () => clearTimeout(t);
  }, [load]);

  // A booking changes who is going, so cached rosters are stale. Dropped rather
  // than refetched: most are never reopened.
  useEffect(() => { setRosters({}); }, [refreshKey]);

  // Page 1 on any filter change: page 4 of the old result set is usually past
  // the end of the new one, which reads as "no sessions" rather than a filter.
  const set = (k, v) => { setPage(1); setFilters(f => ({ ...f, [k]: v })); };

  // A layer has to close the way layers do: click anywhere else, or press
  // Escape. Without this it survives scrolling, filtering and paging.
  useEffect(() => {
    if (openRoster == null) return;
    const away = () => setOpenRoster(null);
    const key  = (e) => { if (e.key === 'Escape') setOpenRoster(null); };
    document.addEventListener('click', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('click', away);
      document.removeEventListener('keydown', key);
    };
  }, [openRoster]);

  const toggleRoster = async (id) => {
    if (openRoster === id) { setOpenRoster(null); return; }
    setOpenRoster(id);
    if (rosters[id]) return;                       // already have it
    try {
      const res = await academyAPI.sessions.roster(id);
      setRosters(r => ({ ...r, [id]: res }));
    } catch {
      setRosters(r => ({ ...r, [id]: { going: [], waitlist: [], failed: true } }));
    }
  };
  /**
   * Release a held place.
   *
   * The list is reloaded rather than patched, because giving up a place changes
   * the spot count, who is shown as booked, and whether the row offers Book at
   * all — three things that would each need mirroring here to stay honest.
   *
   * A paid place takes its money with it. The server cancels the booking and
   * then refunds it, and hands back `message` — the sentence to show, already
   * written for the case that actually happened. Repeating that judgement here
   * would be a second opinion free to drift from the one the server acted on.
   */
  const releasePlace = async (session, player) => {
    setReleasing(session.id);
    try {
      const res = await academyAPI.sessions.cancelBooking(
        session.id,
        player?.self ? {} : { playerId: player?.userId },
      );
      const who = player?.self ? 'Your place' : `${player?.name}'s place`;

      // A refund that did not go through is the one that has to be read, so it
      // is shown as a warning rather than tucked into a success message — the
      // member has to do something about it.
      const failed = res?.refunds?.some(r => r.ok === false);
      if (failed) {
        toast(`${who} is released. ${res.message}`, { icon: '⚠️', duration: 12000 });
      } else if (res?.message) {
        toast.success(`${who} is released. ${res.message}`, { duration: 8000 });
      } else {
        toast.success(`${who} is released.`);
      }
      setCancelling(null);
      setRosters({});
      await load();
    } catch (err) {
      toast.error(err?.message || 'Could not release that place');
    } finally {
      setReleasing(null);
    }
  };

  const activeCount = useMemo(
    () => ['sportTypeId', 'level', 'programType', 'maxPrice', 'to'].filter(k => filters[k]).length
      + (filters.hideFull ? 1 : 0)
      + (filters.days.length ? 1 : 0),
    [filters],
  );

  const inp = `px-3 py-2 border rounded-lg text-sm ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className={`text-xl font-bold ${theme.text}`}>
            {title || (scoped ? 'Sessions you can book' : 'Find a session')}
          </h3>
          <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
            {loading
              ? 'Searching…'
              // "500+" when the scan cap was hit: the number is a floor, and
              // printing it flat would state a total we did not actually count.
              : `${truncated ? `${total}+` : total} session${total === 1 ? '' : 's'} available`}
          </p>
        </div>
        <button
          onClick={() => setShowFilters(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-sm`}
        >
          <SlidersHorizontal size={15} />
          Filters{activeCount ? ` (${activeCount})` : ''}
        </button>
      </div>

      {showFilters && (
        <div className={`p-4 rounded-xl border ${theme.border} mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3`}>
          <div>
            <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>From</label>
            <input type="date" value={filters.from} onChange={e => set('from', e.target.value)} className={`w-full ${inp}`} />
          </div>
          <div>
            <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>To</label>
            <input type="date" value={filters.to} min={filters.from} onChange={e => set('to', e.target.value)} className={`w-full ${inp}`} />
          </div>
          <div>
            <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>Sport</label>
            <select value={filters.sportTypeId} onChange={e => set('sportTypeId', e.target.value)} className={`w-full ${inp}`}>
              <option value="">Any sport</option>
              {sports.map(s => <option key={s.id} value={s.id}>{s.sportName}</option>)}
            </select>
          </div>
          <div>
            <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>Level</label>
            <select value={filters.level} onChange={e => set('level', e.target.value)} className={`w-full ${inp}`}>
              {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </div>
          <div>
            <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>Type</label>
            <select value={filters.programType} onChange={e => set('programType', e.target.value)} className={`w-full ${inp}`}>
              {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>Max price</label>
            <input type="number" min="0" value={filters.maxPrice} onChange={e => set('maxPrice', e.target.value)}
              placeholder="Any" className={`w-full ${inp}`} />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>Days</label>
            {/* Multi-select rather than a dropdown: "Tuesdays and Thursdays" is
                the shape of the question, and picking two from a list of seven
                should not cost two trips through a menu. */}
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map(d => {
                const on = filters.days.includes(d.code);
                return (
                  <button
                    key={d.code}
                    type="button"
                    onClick={() => set('days', on
                      ? filters.days.filter(c => c !== d.code)
                      : [...filters.days, d.code])}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                      on ? 'bg-blue-600 text-white border-transparent' : `${theme.border} ${theme.textSecondary} ${theme.cardHover}`
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
              {filters.days.length > 0 && (
                <button type="button" onClick={() => set('days', [])}
                  className={`px-2 py-1 text-xs ${theme.textMuted} hover:underline`}>
                  Any day
                </button>
              )}
            </div>
          </div>
          <label className={`flex items-center gap-2 text-sm ${theme.text} cursor-pointer sm:col-span-2 lg:col-span-3`}>
            <input type="checkbox" checked={filters.hideFull} onChange={e => set('hideFull', e.target.checked)} className="rounded" />
            Hide sessions that are full
          </label>
        </div>
      )}

      {loading && sessions.length === 0 ? (
        <div className={`flex items-center justify-center gap-2 py-16 ${theme.textMuted}`}>
          <Loader2 size={18} className="animate-spin" /> <span className="text-sm">Finding sessions…</span>
        </div>
      ) : error ? (
        <div className={`text-center py-12 ${theme.textMuted}`}>
          <p className="text-sm">{error}</p>
          <button onClick={load} className={`mt-3 px-4 py-2 rounded-lg text-sm ${theme.buttonSecondary}`}>Retry</button>
        </div>
      ) : sessions.length === 0 ? (
        <div className={`text-center py-14 ${theme.textMuted}`}>
          <Search size={28} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">No sessions match. Try widening the dates or clearing a filter.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sessions.map((s) => {
            const start = new Date(s.startsAt);
            const end   = s.endsAt ? new Date(s.endsAt) : null;
            return (
              <div key={s.id} className={`${theme.card} border rounded-xl p-4`}>
               <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  {/* The session's own name leads. A host who titles a night
                      "Friday Night Lights" means that to be what people see;
                      the date is how they choose between nights, not what the
                      night is called. Always shown — sessions default to the
                      offering's name, and hiding it then left most rows with no
                      title at all. The offering name below stands down instead
                      when the two match. */}
                  {s.title && (
                    <p className={`text-sm font-semibold ${theme.text} truncate`}>{s.title}</p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-semibold ${theme.text}`}>
                      {start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                      <span className={`font-normal ${theme.textSecondary}`}>
                        {' · '}{start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                        {end ? `–${end.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}` : ''}
                      </span>
                    </p>
                    {s.level && (
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${theme.backgroundSecondary} ${theme.textSecondary} capitalize`}>
                        {s.level}
                      </span>
                    )}
                    {s.sportName && (
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${theme.backgroundSecondary} ${theme.textSecondary}`}>
                        {s.sportName}
                      </span>
                    )}
                  </div>
                  <div className={`text-xs ${theme.textMuted} mt-1 flex items-center gap-3 flex-wrap`}>
                    {/* Inside an academy this would be the same name on every
                        row, so it is dropped rather than repeated. */}
                    {!scoped && s.academy?.name && (
                      <span className="flex items-center gap-1"><MapPin size={11} />{s.academy.name}</span>
                    )}
                    <div className="relative inline-flex">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleRoster(s.id); }}
                      className={`flex items-center gap-1 hover:underline ${theme.textMuted}`}
                      title="See who is going"
                    >
                      <Users size={11} />
                      {/* Taken against total, so a player can see how fast it is
                          filling rather than only what is left. Unlimited has no
                          denominator to show — that is not the same statement as
                          zero left. */}
                      {s.capacity == null
                        ? `${s.booked} booked`
                        : `${Math.min(s.booked, s.capacity)}/${s.capacity} booked`}
                      {s.capacity != null && !s.isFull && (
                        <span className={theme.textSecondary}>
                          · {s.spotsLeft} left
                        </span>
                      )}
                      {s.waitlisted > 0 && (
                        <span className="text-amber-600 dark:text-amber-400">
                          · {s.waitlisted} waiting
                        </span>
                      )}
                    </button>

                    {/* A layer rather than an inline block: expanding in place
                        shoved every row below it down the page, so the thing
                        you were reading moved as you opened it. Anchored to the
                        count it belongs to, and dismissed by clicking away. */}
                    {openRoster === s.id && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className={`absolute left-0 top-full mt-1.5 z-30 w-64 p-3 rounded-xl border ${theme.border} ${theme.card} shadow-lg`}
                      >
                        {!rosters[s.id] ? (
                          <p className={`text-xs ${theme.textMuted} flex items-center gap-1.5`}>
                            <Loader2 size={11} className="animate-spin" /> Loading…
                          </p>
                        ) : rosters[s.id].failed ? (
                          <p className={`text-xs ${theme.textMuted}`}>Could not load who is going.</p>
                        ) : (
                          <div className="space-y-2">
                            <RosterList
                              heading={`Going (${rosters[s.id].going.length})`}
                              people={rosters[s.id].going}
                              empty="Nobody yet — be the first."
                              theme={theme}
                            />
                            {rosters[s.id].waitlist.length > 0 && (
                              <RosterList
                                heading={`Waiting (${rosters[s.id].waitlist.length})`}
                                people={rosters[s.id].waitlist}
                                theme={theme}
                                muted
                              />
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    </div>
                    {/* Prefixed, because an offering name sitting bare after the
                        spots reads as a stray label — especially when it does
                        not match the session, as a Tuesday night inside a
                        programme called "Wednesday Open Play" does not. */}
                    {s.programName && s.programName !== s.title && (
                      <span className="truncate">in {s.programName}</span>
                    )}
                    {s.bookedBy?.length > 0 && (
                      <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                        <Check size={11} />
                        {s.bookedBy.map(b => b.name).join(', ')} booked
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  {(() => {
                    // One live price per session, so this is the price rather
                    // than a "from". The tag is the host's own name for it —
                    // "Monday drop-in" says more than the number alone.
                    const price = s.prices?.[0];
                    const fee = feeLine(price);
                    return (
                      <div className="text-right">
                        <p className={`text-sm font-semibold ${theme.text}`}>
                          {s.fromPrice == null ? 'Free' : money(s.fromPrice, s.currency)}
                          {s.fromPrice > 0 && (
                            <span className={`font-normal ${theme.textMuted}`}>
                              {PRICE_UNIT[price?.pricingModel] || '/person'}
                            </span>
                          )}
                        </p>
                        {price?.label && (
                          <p className={`text-[11px] ${theme.textMuted} truncate max-w-[9rem]`}>{price.label}</p>
                        )}
                        {fee && (
                          <p className={`text-[11px] ${theme.textMuted}`}>{fee} with fee</p>
                        )}
                      </div>
                    );
                  })()}
                  {(() => {
                    // Everyone in the household already has a place here, so
                    // there is nothing left to buy. Saying "Book" and refusing
                    // at the last step of checkout is a dead end — the player
                    // cannot act on it, and by then they have picked a payment
                    // method for a booking that was never going to happen.
                    const nobodyLeft = Array.isArray(s.bookableBy) && s.bookableBy.length === 0;
                    if (nobodyLeft) {
                      return (
                        <span
                          data-testid={`booked-session-${s.id}`}
                          className={`px-4 py-2 rounded-lg text-sm font-medium border ${theme.border} ${theme.textMuted} inline-flex items-center gap-1.5`}
                        >
                          <Check size={14} /> Booked
                        </span>
                      );
                    }
                    return (
                      <button
                        onClick={() => onBook?.(s)}
                        // Addressable per session. Titles repeat across a series
                        // and the card markup carries no role, so a test
                        // otherwise clicks "the first Book button" and hopes.
                        data-testid={`book-session-${s.id}`}
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${
                          s.isFull ? `border ${theme.border} ${theme.text} ${theme.cardHover}` : theme.buttonPrimary}`}
                      >
                        <Ticket size={14} className="inline mr-1.5" />
                        {s.isFull ? 'Join waitlist' : 'Book'}
                      </button>
                    );
                  })()}
                </div>
               </div>

               {/* Giving a place back.
                   The Booked chip used to be the end of the road: once you held
                   a place there was nothing on this screen that could release
                   it, so the only way out was to ask the venue. It sits under
                   the row as plain text rather than beside Book — releasing a
                   place is a considered act, not something to fat-finger while
                   reaching for the primary button. */}
               {s.bookedBy?.length > 0 && (
                 cancelling?.sessionId === s.id ? (
                   <div className={`mt-3 pt-3 border-t ${theme.border}`}>
                     {/* Whose place, when a household holds more than one. The
                         single case skips the question entirely. */}
                     {s.bookedBy.length > 1 && (
                       <div className="flex flex-wrap gap-1.5 mb-2">
                         {s.bookedBy.map(b => (
                           <button
                             key={b.userId}
                             onClick={() => setCancelling({ sessionId: s.id, player: b })}
                             className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                               cancelling.player?.userId === b.userId
                                 ? 'bg-red-600 text-white border-transparent'
                                 : `${theme.border} ${theme.textSecondary} ${theme.cardHover}`
                             }`}
                           >
                             {b.self ? 'You' : b.name}
                           </button>
                         ))}
                       </div>
                     )}
                     <p className={`text-xs ${theme.textMuted} mb-2`}>
                       Give up {cancelling.player?.self ? 'your' : `${cancelling.player?.name}'s`} place
                       {s.fromPrice > 0 && (
                         <>
                           {' '}— if it has already been paid for, the money stays with the
                           venue as credit for this session. Ask them for a refund if you
                           will not be using it.
                         </>
                       )}
                     </p>
                     <div className="flex gap-2">
                       <button
                         onClick={() => setCancelling(null)}
                         className={`px-3 py-1.5 rounded-lg text-xs font-medium ${theme.buttonGhost}`}
                       >
                         Keep it
                       </button>
                       <button
                         onClick={() => releasePlace(s, cancelling.player)}
                         disabled={releasing === s.id}
                         data-testid={`confirm-cancel-session-${s.id}`}
                         className={`px-3 py-1.5 rounded-lg text-xs font-medium ${theme.buttonDanger} inline-flex items-center gap-1.5`}
                       >
                         {releasing === s.id && <Loader2 size={12} className="animate-spin" />}
                         Give up the place
                       </button>
                     </div>
                   </div>
                 ) : (
                   <button
                     onClick={() => setCancelling({ sessionId: s.id, player: s.bookedBy[0] })}
                     data-testid={`cancel-session-${s.id}`}
                     className={`mt-2 text-xs ${theme.textMuted} hover:text-red-600 dark:hover:text-red-400`}
                   >
                     {s.bookedBy.length > 1 ? 'Cancel a place' : 'Cancel booking'}
                   </button>
                 )
               )}
              </div>
            );
          })}
        </div>
      )}

      {/* Paging over the server's filtered total, so the last page is the real
          last page. Hidden in compact mode, which is a teaser, not a browser. */}
      {!compact && total > PAGE_SIZE && (
        <div className="flex items-center justify-center gap-3 pt-4">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
            className={`p-2 rounded-lg border ${theme.border} ${page === 1 ? 'opacity-30 cursor-not-allowed' : theme.cardHover}`}
            aria-label="Previous page"
          >
            <ChevronLeft size={16} className={theme.text} />
          </button>
          <span className={`text-sm ${theme.textMuted}`}>
            Page {page} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={page >= Math.ceil(total / PAGE_SIZE) || loading}
            className={`p-2 rounded-lg border ${theme.border} ${page >= Math.ceil(total / PAGE_SIZE) ? 'opacity-30 cursor-not-allowed' : theme.cardHover}`}
            aria-label="Next page"
          >
            <ChevronRight size={16} className={theme.text} />
          </button>
        </div>
      )}
    </div>
  );
};

export default SessionFinder;
